/**
 * Scheduled Booking Types
 * 
 * These types represent bookings created by the Resource Scheduler
 * that auto-populate into employee timesheets.
 */

export interface ScheduledBooking {
  id: number;
  employeeId: number;
  employeeName: string;
  projectId: string;
  projectName: string;
  projectCode: string;
  costCentreId: string;
  costCentreName: string;
  roleId?: number;
  roleName?: string;
  companyId: string;
  weekEnding: Date;
  
  // Scheduled hours per day
  scheduledHours: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  
  // Metadata
  createdBy: string; // Resource Manager who created the booking
  createdByName: string;
  createdAt: Date;
  notes?: string; // Optional notes from scheduler
  
  // Status
  status: 'active' | 'dismissed' | 'completed' | 'cancelled';
}

/**
 * Extended Timesheet Entry with Scheduling Information
 * 
 * This extends the base TimesheetEntry to include scheduling metadata
 */
export interface ScheduledTimesheetEntry {
  // Base timesheet entry fields
  id: string;
  projectId: string;
  costCentreId: string;
  description: string;
  hours: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  
  // Scheduling fields
  isFromSchedule: boolean;
  scheduledBookingId?: number;
  
  // Original scheduled hours (for comparison)
  scheduledHours?: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  
  // Dismissal tracking
  isDismissed: boolean;
  dismissedAt?: Date;
  dismissalReason?: string;
  
  // Booking metadata (for info display)
  bookingCreatedBy?: string;
  bookingCreatedByName?: string;
  bookingNotes?: string;
  bookingCreatedAt?: Date;
}

/**
 * Dismissal Reason Options
 */
export type DismissalReason = 
  | 'didnt-work'
  | 'cancelled'
  | 'incorrect'
  | 'different-project'
  | 'other';

export interface DismissalReasonOption {
  value: DismissalReason;
  label: string;
}

export const DISMISSAL_REASONS: DismissalReasonOption[] = [
  { value: 'didnt-work', label: "Didn't work on this project this week" },
  { value: 'cancelled', label: 'Project was cancelled/postponed' },
  { value: 'incorrect', label: 'Scheduled incorrectly' },
  { value: 'different-project', label: 'Worked on different project instead' },
  { value: 'other', label: 'Other' },
];
